import React from 'react';
import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';
import classNames from 'classnames';
import { gettext, translationPropType } from '@eventbrite/i18n';

import { InputField } from '@eventbrite/eds-input-field';
import { SelectField } from '@eventbrite/eds-input-field';
import { STYLE_BASIC, TYPE_TEXT } from '@eventbrite/eds-input-field';
import { BULK_ACTIONS_PROP_TYPE } from './constants';

import { getSelectPropValues } from '@eventbrite/eds-select';

export default class DataTableActionBar extends React.Component {
    static propTypes = {
        /**
         * array of available bulk actions to render in a select
         **/
        bulkActions: BULK_ACTIONS_PROP_TYPE,
        /**
         * array of available select filter actions to render in a select
         **/
        selectFilterActions: BULK_ACTIONS_PROP_TYPE,
        /**
         * initial placeholder string on the text filter
         **/
        filterPlaceholder: translationPropType,
        /**
         * initial string given to filter the items
         **/
        filterText: PropTypes.string,
        /**
         * whether or not the bar should contain filter
         **/
        hideTextFilter: PropTypes.bool,
        /**
         * whether or not the bulk actions should be disabled, e.g. if one or more items are selected
         **/
        isBulkActionEnabled: PropTypes.bool,
        /**
         * whether or not the Button should be disabled
         **/
        isButtonEnabled: PropTypes.bool,
        /**
         * whether or not the SelectFilter should be disabled
         **/
        isSelectFilterEnabled: PropTypes.bool,
        /**
         * callback that is passed with the value of the selected bulk action name
         * i.e. (value) => { console.log(value); // 'delete' }
         **/
        onBulkActionSelect: PropTypes.func,
        /**
         * callback that is passed with the value of the selected filter
         * i.e. (value) => { console.log(value); // 1 => with 1 representing a status for PromotedListingsTable for example }
         **/
        onSelectFilterSelect: PropTypes.func,
        /**
         * callback that is passed with the input value when the text filter is on blur
         * i.e. (value) => { console.log(value); // 'n' }
         **/
        onTextFilterBlur: PropTypes.func,
        /**
         * callback that is passed with the current value of text filter input after it changes
         * i.e. (value) => { console.log(value); // 'name' }
         **/
        onTextFilterChange: PropTypes.func,
    };

    static defaultProps = {
        filterText: '',
        filterPlaceholder: gettext('Enter text to filter'),
        hideTextFilter: false,
        isBulkActionEnabled: false,
        isButtonEnabled: false,
        isSelectFilterEnabled: false,
    };

    _handleDataAction(type, value) {
        const dataActionHandler = this.props[type];

        if (dataActionHandler) {
            dataActionHandler(value);
        }
    }

    _renderSelectFilter() {
        // Although _renderSelectFilter is similar to _renderBulkActionSelect, rather than combining them into something more general
        // it will be less confusing to create two seperate functions.
        const {
            isSelectFilterEnabled,
            isBulkActionEnabled,
            showTextFilter,
            selectFilterActions,
        } = this.props;
        // We only want this to appear with TextFilter turned on AND BulkAction turned off
        if (
            !isSelectFilterEnabled ||
            (isBulkActionEnabled && !showTextFilter)
        ) {
            return null;
        }

        const values = getSelectPropValues(
            selectFilterActions,
            'label',
            'action',
        );
        const columnClassName = classNames(
            'eds-g-cell',
            'eds-g-cell-1-1',
            'eds-g-cell-md-2-12',
            'eds-g-cell-sm-12-12',
            'eds-l-mar-bot-2',
            'eds-fx--fade-in',
        );

        return (
            <div className={columnClassName}>
                <SelectField
                    data-spec="data-action-bar-select-filter"
                    values={values}
                    label={gettext('Filter by Status')}
                    placeholder={gettext('Filter by Status')}
                    value={null}
                    disabled={!isSelectFilterEnabled}
                    onChange={this._handleDataAction.bind(
                        this,
                        'onSelectFilterSelect',
                    )}
                    style={STYLE_BASIC}
                />
            </div>
        );
    }

    _renderButton() {
        const {
            buttonText,
            isButtonEnabled,
            isBulkActionEnabled,
            showTextFilter,
        } = this.props;
        // We only want this to appear with TextFilter turned on AND BulkAction turned off
        if (!isButtonEnabled || (isBulkActionEnabled && !showTextFilter)) {
            return null;
        }

        const buttonClassName = classNames(
            'eds-btn',
            'eds-btn--button',
            'eds-btn--fill',
            'eds-g-cell',
            'eds-g-cell-1-1',
            'eds-g-cell-md-3-12',
            'eds-g-cell-sm-12-12',
            'eds-l-mar-bot-2',
            'eds-fx--fade-in',
        );

        return (
            <button
                className={buttonClassName}
                data-spec="data-action-bar-button"
                onClick={this._handleDataAction.bind(this, 'onButtonClick')}
            >
                <span style={{ fontWeight: 600 }}>{buttonText}</span>
            </button>
        );
    }

    _renderBulkActionSelect() {
        const { bulkActions, isBulkActionEnabled, hideTextFilter } = this.props;

        if (isEmpty(bulkActions) || !isBulkActionEnabled) {
            return null;
        }

        const values = getSelectPropValues(bulkActions, 'label', 'action');
        const columnClassName = classNames(
            'eds-g-cell',
            'eds-g-cell-1-1',
            'eds-g-cell-md-4-12',
            'eds-l-mar-bot-2',
            'eds-fx--fade-in',
            {
                'eds-g-offset-md-8-12': hideTextFilter,
                'eds-g-offset-md-2-12': !hideTextFilter,
            },
        );

        return (
            <div className={columnClassName}>
                <SelectField
                    data-spec="data-action-bar-bulkaction"
                    values={values}
                    label={gettext('Bulk Actions')}
                    placeholder={gettext('Choose Bulk Action')}
                    value={null}
                    disabled={!isBulkActionEnabled}
                    onChange={this._handleDataAction.bind(
                        this,
                        'onBulkActionSelect',
                    )}
                    style={STYLE_BASIC}
                />
            </div>
        );
    }

    _renderTextFilter() {
        const { hideTextFilter, filterPlaceholder, filterText } = this.props;

        if (hideTextFilter) {
            return null;
        }

        const columnClassName = classNames(
            'eds-g-cell',
            'eds-g-cell-1-1',
            'eds-g-cell-md-6-12',
            'eds-l-mar-bot-2',
        );

        return (
            <div className={columnClassName}>
                <InputField
                    id="action-bar-textfilter"
                    data-spec="data-action-bar-textfilter"
                    label={gettext('Filter')}
                    type={TYPE_TEXT}
                    style={STYLE_BASIC}
                    placeholder={filterPlaceholder}
                    value={filterText}
                    onChange={this._handleDataAction.bind(
                        this,
                        'onTextFilterChange',
                    )}
                    onBlur={this._handleDataAction.bind(
                        this,
                        'onTextFilterBlur',
                    )}
                />
            </div>
        );
    }

    render() {
        const { isButtonEnabled, isSelectFilterEnabled } = this.props;
        const textFilter = this._renderTextFilter();
        const bulkActionSelect = this._renderBulkActionSelect();
        const selectFilter = this._renderSelectFilter();
        const button = this._renderButton();
        const hasSpaceBetween =
            isButtonEnabled || isSelectFilterEnabled
                ? 'eds-align--space-between'
                : '';
        const containerClassNames = classNames(
            'eds-data-table-action-bar',
            'eds-g-grid',
            hasSpaceBetween,
        );

        return (
            <div className={containerClassNames}>
                {textFilter}
                {bulkActionSelect}
                {selectFilter}
                {button}
            </div>
        );
    }
}
